import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  delay,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';
import { ErrorService } from 'src/app/services/error.service';
import { ExportDataService } from 'src/app/services/export-data.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import * as UserActions from '../actions/user.actions';
import { AppState } from '../app.reducer';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private userService: UserService,
    private router: Router,
    private errorService: ErrorService,
    private exportDataService: ExportDataService
  ) {}

  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.readUsers),
      switchMap(({ payload }) => {
        this.store.dispatch(
          UserActions.userLoading({ payload: { loading: true } })
        );
        return this.userService.readUsers(payload.query).pipe(
          map((response) => ({ payload, response })),
          mergeMap((mappedResponse) => {
            this.store.dispatch(
              UserActions.usersRead({
                payload: {
                  users: mappedResponse.response.data,
                  query: mappedResponse.payload.query,
                  totalCount: +mappedResponse.response.totalCount,
                },
              })
            );
            return of(UserActions.userLoading({ payload: { loading: false } }));
          }),
          catchError((error) => {
            return of(
              UserActions.userActionFail({
                payload: { error: this.errorService.mapError(error) },
              })
            );
          })
        );
      })
    )
  );

  getUserById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.readUserById),
      switchMap(({ payload }) => {
        return this.userService.readUserById(payload.userId).pipe(
          mergeMap((response) => {
            return of(
              UserActions.userByIdRead({ payload: { user: response.data } })
            );
          }),
          catchError((error) => {
            this.store.dispatch(UserActions.redirectToUsersList());
            return of(
              UserActions.userActionFail({
                payload: { error: this.errorService.mapError(error) },
              })
            );
          })
        );
      })
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUser),
      switchMap(({ payload }) => {
        this.store.dispatch(
          UserActions.userLoading({ payload: { loading: true } })
        );
        return this.userService.updateUser(payload.user).pipe(
          map((response) => ({ payload, response })),
          mergeMap(() => {
            this.store.dispatch(UserActions.userUpdated());
            return of(UserActions.userLoading({ payload: { loading: false } }));
          }),
          catchError((error) => {
            if (environment.optimisticLockErrors.includes(error.error?.name))
              return of(
                UserActions.userOptimisticLockError({
                  payload: {
                    optimisticLockError: this.errorService.mapError(error),
                  },
                })
              );
            else
              return of(
                UserActions.userActionFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
          })
        );
      })
    )
  );

  redirectToUsersList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.redirectToUsersList),
        tap(async () => {
          await this.router.navigate(['/security/users']);
        })
      ),
    { dispatch: false }
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteUser),
      switchMap(({ payload }) => {
        this.store.dispatch(
          UserActions.userLoading({ payload: { loading: true } })
        );
        return this.userService
          .deleteUser(payload.user_id, payload.version)
          .pipe(
            mergeMap(() => {
              this.store.dispatch(UserActions.userDeleted());
              return of(
                UserActions.userLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              if (environment.optimisticLockErrors.includes(error.error?.name))
                return of(
                  UserActions.userOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              else
                return of(
                  UserActions.userActionFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
            })
          );
      })
    )
  );
}
